/**
 * /accept-invite?token=... — public route. Lets a newly-invited user set
 * their own password and get signed in. No admin involvement after the
 * invite link is shared.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as adminApi from '../api/admin';
import { ApiError } from '../api/client';

export default function AcceptInvitePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.search).get('token') ?? '';

  const [info, setInfo] = useState<{ email: string; display_name: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setErr('Missing token.');
      return;
    }
    adminApi
      .getInviteInfo(token)
      .then(setInfo)
      .catch((e) => {
        const msg = e instanceof ApiError ? e.message : 'invalid invite';
        setErr(msg);
      });
  }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pw !== confirm) {
      setErr('Passwords do not match.');
      return;
    }
    if (pw.length < 12) {
      setErr('Password must be at least 12 characters.');
      return;
    }
    setBusy(true);
    try {
      await adminApi.acceptInvite(token, pw);
      navigate('/', { replace: true });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'failed';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        <div className="login-brand">Hensley Heritage</div>
        <div className="login-sub">Accept your invite</div>
        {err && <div className="login-error" style={{ marginBottom: 12 }}>{err}</div>}
        {info && (
          <form className="login-form" onSubmit={submit}>
            <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 4 }}>
              Setting up: <strong>{info.email}</strong> ({info.display_name})
            </div>
            <div className="field">
              <label className="field-label">Choose a password</label>
              <input
                className="field-input"
                type="password"
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                minLength={12}
              />
            </div>
            <div className="field">
              <label className="field-label">Confirm</label>
              <input
                className="field-input"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={12}
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {busy ? 'Activating…' : 'Set password & sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
